import { lazy, Suspense, useEffect, useRef } from "react";
// App.css (alte Gestaltung) wird nicht mehr geladen, die drei noch genutzten Regeln stehen in index.css
import { BrowserRouter, HashRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";

// Für Vorschauen ohne eigenen Server (z. B. Freigabe Links) mit REACT_APP_HASH_ROUTER=1 bauen.
const Router = process.env.REACT_APP_HASH_ROUTER === "1" ? HashRouter : BrowserRouter;
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/i18n/ThemeContext";
import Navbar from "@/components/Navbar";
import ScrollChrome from "@/components/relaunch/ScrollChrome";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import DocumentTitle from "@/components/DocumentTitle";
import { VersionProvider, useVersion } from "@/version/VersionContext";
import PreviewDock from "@/version/PreviewDock";
import EmbedBridge from "@/version/EmbedBridge";
import { localeFromPath } from "@/i18n/localePath";
import { MotionConfig } from "framer-motion";
import { useMotionOff } from "@/version/motionPref";

// framer-motion Übergänge folgen der Einstellung „Animationen aus“ (Version 3) sofort
const MotionGate = ({ children }) => {
    const off = useMotionOff();
    return <MotionConfig reducedMotion={off ? "always" : "user"}>{children}</MotionConfig>;
};

// Relaunch 2026: Insights, Rollen und Infrastruktur leiten auf die neue Struktur um.
// Die alten Seitendateien bleiben im Code, damit sie bei Bedarf wieder eingebunden werden können.

// Scrollposition: neue Seite oben (oder am Anker), bei Zurück und Vor die gemerkte Position je Verlaufseintrag.
// Positionen liegen je location.key in sessionStorage; der Browser stellt selbst nichts wieder her (scrollRestoration manual).
const SCROLL_KEY = "parto-scroll";
const readScroll = () => { try { return JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "{}"); } catch { return {}; } };
const writeScroll = (m) => { try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify(m)); } catch { /* ohne Speicher keine Wiederherstellung */ } };

const ScrollManager = () => {
    const { pathname, hash, key } = useLocation();
    const navType = useNavigationType();
    const keyRef = useRef(key);

    useEffect(() => {
        try { if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"; } catch { /* egal */ }
    }, []);

    // Aktuelle Position laufend dem aktiven Verlaufseintrag zuordnen
    useEffect(() => {
        keyRef.current = key;
        let t = null;
        const save = () => {
            if (t) return;
            t = setTimeout(() => { t = null; const m = readScroll(); m[keyRef.current] = window.scrollY; const keys = Object.keys(m); if (keys.length > 60) delete m[keys[0]]; writeScroll(m); }, 150);
        };
        window.addEventListener("scroll", save, { passive: true });
        return () => { window.removeEventListener("scroll", save); if (t) clearTimeout(t); };
    }, [key]);

    useEffect(() => {
        // Zurück oder Vor: gemerkte Position, sobald die Seite hoch genug ist (Inhalte können nachladen)
        if (navType === "POP") {
            const y = readScroll()[key];
            if (typeof y === "number") {
                let tries = 0, raf = 0;
                const restore = () => {
                    const max = document.documentElement.scrollHeight - window.innerHeight;
                    if (max >= y - 2 || tries++ > 60) { window.scrollTo({ top: Math.min(y, Math.max(max, 0)), behavior: "instant" }); return; }
                    raf = requestAnimationFrame(restore);
                };
                raf = requestAnimationFrame(restore);
                return () => cancelAnimationFrame(raf);
            }
        }
        if (hash) {
            let el = null;
            try { el = document.getElementById(decodeURIComponent(hash.slice(1))); } catch { el = null; }
            if (el) {
                const id = setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
                return () => clearTimeout(id);
            }
        }
        window.scrollTo({ top: 0, behavior: "instant" });
        return undefined;
    }, [pathname, hash, key, navType]);
    return null;
};

const LocaleSync = () => {
    const { pathname } = useLocation();
    const { lang: contentLang, uiLang, setLang } = useLanguage();
    const lang = uiLang || contentLang; // Version 4: Sprache der Adresse
    useEffect(() => {
        const pathLanguage = localeFromPath(pathname);
        if (lang !== pathLanguage) setLang(pathLanguage);
    }, [lang, pathname, setLang]);
    return null;
};

const SMOOTH_SCROLL = false;

const LenisRoot = ({ children }) => {
    useEffect(() => {
        // In eingebetteten Vorschauen (iframe) fängt Lenis das Mausrad ab, dort normal scrollen.
        let embedded = false;
        try { embedded = window.self !== window.top; } catch (e) { embedded = true; }
        // Weiches Nachlaufen beim Scrollen ist ausgeschaltet: Die Seite reagiert sofort wie gewohnt.
        // Wieder einschalten: SMOOTH_SCROLL auf true setzen.
        if (embedded || !SMOOTH_SCROLL) return undefined;
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);
    return children;
};

// Routen je Version als eigene Pakete (React.lazy): Die Livefassung lädt nur den Code ihrer Version,
// die Vorschau lädt beim Umschalten die andere Version nach. Inhalt der Routen unverändert in
// src/version/RoutesV1.jsx, src/version/RoutesV2.jsx und src/v3/RoutesV3.jsx.
const SiteRoutes = lazy(() => import("@/version/RoutesV2"));
const V1Routes = lazy(() => import("@/version/RoutesV1"));
const V3Routes = lazy(() => import("@/v3/RoutesV3"));
const V4Routes = lazy(() => import("@/v4/Routes"));
// Version 5: eigenständige Landingpage Überbestände (eigene Kopf und Fußzeile, kein Menü)
const V5Routes = lazy(() => import("@/v5/Routes"));

// Platzhalter, solange ein Routenpaket lädt (hält die Fußzeile unten, kein Springen)
const RoutesFallback = () => <main className="min-h-[100vh]" aria-busy="true" />;

// Gemeinsame Seitenteile (Menü, Fußzeile, Titel, Scrollhilfen) in Version 5 ausblenden; die Landingpage bringt eigene mit
const NotV5 = ({ children }) => (useVersion().version === "5" ? null : children);

const VersionedRoutes = () => {
    const { version } = useVersion();
    const R = version === "5" ? V5Routes : version === "4" ? V4Routes : version === "3" ? V3Routes : version === "1" ? V1Routes : SiteRoutes;
    return <Suspense fallback={<RoutesFallback />}><R /></Suspense>;
};

function App() {
    return (
        <div className="App bg-petrol-950 min-h-screen">
            
            <Router>
                <VersionProvider><MotionGate>
                <ThemeProvider>
                <LanguageProvider>
                        <LenisRoot>
                            <LocaleSync />
                            <ScrollManager />
                            <NotV5><DocumentTitle /></NotV5>
                            <NotV5><Seo /></NotV5>
                            <NotV5><Navbar /></NotV5>
                            <NotV5><ScrollChrome /></NotV5>
                        <Routes>
                            <Route path="/en/*" element={<VersionedRoutes />} />
                            <Route path="/*" element={<VersionedRoutes />} />
                        </Routes>
                        <NotV5><Footer /></NotV5>
                    </LenisRoot>
                <Toaster theme="system" position="bottom-right" />
                <PreviewDock />
                <EmbedBridge />
                </LanguageProvider>
                </ThemeProvider>
                </MotionGate></VersionProvider>
            </Router>
        </div>
    );
}

export default App;
